// File: src/store/slices/appointmentsSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Appointment, AppointmentStatusValue } from '../../types/appointment';
import appointmentService from '../../services/appointmentService';
import { mergeAppointmentRecord } from '../../utils/appointmentRealtime';

interface AppointmentsState {
  appointments: Appointment[];
  currentAppointment: Appointment | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: AppointmentsState = {
  appointments: [],
  currentAppointment: null,
  isLoading: false,
  error: null,
};

// Fetch all appointments
export const fetchAppointments = createAsyncThunk<
  Appointment[],
  { silent?: boolean } | void,
  { rejectValue: string }
>(
  'appointments/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await appointmentService.getAppointments();
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch appointments');
    }
  }
);

// Fetch single appointment
export const fetchAppointmentById = createAsyncThunk<
  Appointment,
  string,
  { rejectValue: string }
>(
  'appointments/fetchById',
  async (appointmentId, { rejectWithValue }) => {
    try {
      console.log('[AppointmentsSlice] Fetching appointment by ID:', appointmentId);
      const response = await appointmentService.getAppointment(appointmentId);
      console.log('[AppointmentsSlice] Appointment fetched:', response);
      return response;
    } catch (error: any) {
      console.error('[AppointmentsSlice] Error fetching appointment:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch appointment';
      return rejectWithValue(errorMessage);
    }
  }
);

// Update status (including cancel and no-show)
export const updateAppointmentStatus = createAsyncThunk<
  Appointment,
  { appointmentId: string; status: AppointmentStatusValue; reason?: string },
  { rejectValue: string }
>(
  'appointments/updateStatus',
  async ({ appointmentId, status, reason }, { rejectWithValue }) => {
    try {
      switch (status) {
        case 'cancelled':
          return await appointmentService.cancelAppointment(appointmentId, { reason: reason || 'cancelled' });
        case 'no-show':
          return await appointmentService.markNoShow(appointmentId, reason);
        default:
          return await appointmentService.updateAppointmentStatus(appointmentId, { status, reason });
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update appointment status');
    }
  }
);

//* NOTE: service currently does not support date/time updates; implement this when available
// const rescheduleAppointment = createAsyncThunk<Appointment, { appointmentId: string; date: string; time: string }, { rejectValue: string }>(
//   'appointments/reschedule',
//   async ({ appointmentId, date, time }, { rejectWithValue }) => {
//     try {
//       // TODO: appointmentService.reschedule or updateAppointment
//       throw new Error('Reschedule not implemented in service');
//     } catch (error: any) {
//       return rejectWithValue(error.message || 'Failed to reschedule appointment');
//     }
//   }
// );

const appointmentsSlice = createSlice({
  name: 'appointments',
  initialState,
  reducers: {
    clearCurrentAppointment: (state) => {
      state.currentAppointment = null;
    },
    // Applied when the socket emits an appointment_update event from the server
    socketAppointmentUpdated: (state, action: PayloadAction<Appointment>) => {
      const updated = action.payload;
      const updatedId = String(updated?._id || (updated as { id?: string })?.id || '');
      if (!updatedId) return;
      const idx = state.appointments.findIndex((item) => String(item._id || (item as { id?: string }).id) === updatedId);
      if (idx !== -1) {
        state.appointments[idx] = mergeAppointmentRecord(state.appointments[idx], updated);
      } else {
        state.appointments.push(updated);
      }
      const currentId = state.currentAppointment
        ? String(state.currentAppointment._id || (state.currentAppointment as { id?: string }).id)
        : '';
      if (currentId && currentId === updatedId) {
        state.currentAppointment = mergeAppointmentRecord(state.currentAppointment, updated);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAppointments.pending, (state, action) => {
        const silent = Boolean(action.meta.arg && action.meta.arg.silent);
        if (!silent) state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAppointments.fulfilled, (state, action: PayloadAction<Appointment[]>) => {
        state.isLoading = false;
        state.appointments = action.payload;
      })
      .addCase(fetchAppointments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAppointmentById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAppointmentById.fulfilled, (state, action: PayloadAction<Appointment>) => {
        state.isLoading = false;
        state.currentAppointment = action.payload;
      })
      .addCase(fetchAppointmentById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateAppointmentStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateAppointmentStatus.fulfilled, (state, action: PayloadAction<Appointment>) => {
        state.isLoading = false;
        state.currentAppointment = action.payload;
        const idx = state.appointments.findIndex(a => a._id === action.payload._id);
        if (idx !== -1) state.appointments[idx] = action.payload;
      })
      .addCase(updateAppointmentStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearCurrentAppointment, socketAppointmentUpdated } = appointmentsSlice.actions;
export default appointmentsSlice.reducer;
